import React, { useMemo, useRef, useState } from "react";

import {
  ArrowDownUp,
  EyeOff,
  GripVertical,
  Play,
  Plus,
  Power,
  RotateCcw,
  Save,
  SearchX,
  Trash2,
  UserCheck,
} from "lucide-react";
import { createPortal } from "react-dom";

import AssignVideosModal from "./AssignVideosModal";
import DeactivateVideoModal from "./DeactivateVideoModal";
import EmptyState from "./EmptyState";
import FilterChips from "./FilterChips";
import ItemActionsMenu from "./ItemActionsMenu";
import LayoutToggle, { VideoLayout } from "./LayoutToggle";
import ReorderControls from "./ReorderControls";
import { runSmoothReorder, useReorderHighlight } from "./reorderMotion";
import { moveItemInArray, type ReorderDirection } from "./reorderUtils";
import RoleVideoGrid from "./RoleVideoGrid";
import SortableVideoGrid from "./SortableVideoGrid";
import useMagneticSections from "./useMagneticSections";
import {
  ACCOUNT_TEAM_ASSIGNMENT_ROLES,
  DEFAULT_ACCOUNT_TEAM_SUBROLE,
  getVideoRoleKey,
  isVideoAccountTeamAllSubrole,
  isVideoAccountTeamTopRole,
  normalizeVideos,
  recalculateVideoOrders,
  VIDEO_ACCOUNT_TEAM_SUBROLE_OPTIONS,
  VIDEO_ROLE_ACCORDIONS,
  VIDEO_ROLE_TOP_OPTIONS,
} from "./videoOrderUtils";
import VideoPreviewModal from "./VideoPreviewModal";
import WorkspaceSearch from "./WorkspaceSearch";
import Button from "../../Components/Button";
import DropdownSwitcher from "../../Components/DropdownSwitcher";
import CommonModal from "../../Components/Modal";
import TruncatedTooltip from "../../Components/TruncatedTooltip";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  deleteVideo,
  fetchAdminVideos,
  fetchLibraryVideos,
  reorderAdminVideos,
  updateVideoActiveStatus,
  Video,
} from "../../Store/Reducers/VideoSlice";
import { toast } from "../../Utils/Helper/toast";
import {
  groupVideosByTopic,
  UNCATEGORIZED_TOPIC_KEY,
  VideoTopicGroup,
} from "../../Utils/Helper/videoTopics";
import { getYouTubeThumbnailUrl } from "../../Utils/Helper/videoUrl";

type VideoGroup = VideoTopicGroup<Video>;

const ALL_TOPICS_KEY = "all";
const DEFAULT_TOP_ROLE =
  VIDEO_ROLE_TOP_OPTIONS.find(role => role.value === "vendor_admin")?.value ||
  VIDEO_ROLE_TOP_OPTIONS[0]?.value ||
  "vendor_admin";

const toTopicGroups = (videos: Video[]): VideoGroup[] =>
  groupVideosByTopic(videos, "Uncategorized").map(group => ({
    ...group,
    videos: recalculateVideoOrders(group.videos),
  }));

const flattenIds = (groups: VideoGroup[]) =>
  groups.flatMap(group => group.videos.map(video => video.video_id));

const getRoleLabel = (roleKey: string) =>
  VIDEO_ACCOUNT_TEAM_SUBROLE_OPTIONS.find(role => role.value === roleKey)
    ?.label ||
  VIDEO_ROLE_ACCORDIONS.find(role => role.value === roleKey)?.label ||
  VIDEO_ROLE_TOP_OPTIONS.find(role => role.value === roleKey)?.label ||
  roleKey;

const getTopRoleLabel = (roleKey: string) =>
  VIDEO_ROLE_TOP_OPTIONS.find(role => role.value === roleKey)?.label || roleKey;

const resolveAssignmentRoles = (topRole: string, subRole: string): string[] => {
  if (!isVideoAccountTeamTopRole(topRole)) return [topRole];
  if (isVideoAccountTeamAllSubrole(subRole)) {
    return [...ACCOUNT_TEAM_ASSIGNMENT_ROLES];
  }
  return [subRole];
};

/** Union of role playlists, deduped by library video (browse / assign scope). */
const mergeVideosAcrossRoles = (
  videosByRole: Record<string, Video[]>,
  roles: string[]
): Video[] => {
  const seen = new Set<string>();
  const merged: Video[] = [];
  roles.forEach(role => {
    (videosByRole[role] || []).forEach(video => {
      const key = video.library_video_id || video.video_id;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(video);
    });
  });
  return merged;
};

const getVideoRoleLabel = (video: Video) =>
  getRoleLabel(getVideoRoleKey(video.role));

const matchesSearch = (video: Video, query: string) =>
  !query ||
  (video.title || "").toLowerCase().includes(query) ||
  (video.description || "").toLowerCase().includes(query);

const noop = () => undefined;

interface AssignmentsPanelProps {
  actionsSlot: HTMLElement | null;
}

const AssignmentsPanel: React.FC<AssignmentsPanelProps> = ({ actionsSlot }) => {
  const dispatch = useAppDispatch();
  const {
    videos,
    loading,
    reorderVideosLoading,
    libraryVideos: libraryItems,
  } = useAppSelector(state => state.video);

  const canvasRef = useRef<HTMLDivElement>(null);
  const listReorderRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const { highlightId, flash, isHighlighted } = useReorderHighlight();
  const [selectedTopRole, setSelectedTopRole] =
    useState<string>(DEFAULT_TOP_ROLE);
  const [selectedSubRole, setSelectedSubRole] = useState<string>(
    DEFAULT_ACCOUNT_TEAM_SUBROLE
  );
  const [topicFilter, setTopicFilter] = useState(ALL_TOPICS_KEY);
  const [scrollRequestId, setScrollRequestId] = useState(0);
  const [search, setSearch] = useState("");
  const [layout, setLayout] = useState<VideoLayout>("cards");
  const [assignOpen, setAssignOpen] = useState(false);
  const [editGroups, setEditGroups] = useState<VideoGroup[] | null>(null);
  const [previewVideo, setPreviewVideo] = useState<Video | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Video | null>(null);
  const [removing, setRemoving] = useState(false);
  const [statusTarget, setStatusTarget] = useState<{
    video: Video;
    toActive: boolean;
  } | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);

  const videosByRole = useMemo(() => {
    const grouped: Record<string, Video[]> = {};
    normalizeVideos(videos as Video[]).forEach(video => {
      const roleKey = getVideoRoleKey(video.role);
      grouped[roleKey] = [...(grouped[roleKey] || []), video];
    });
    return grouped;
  }, [videos]);

  const isEditing = editGroups !== null;
  // Same as Video Library: stacked topic sections + left nav scroll sync.
  const isMagnetic = true;
  const query = search.trim().toLowerCase();
  const assignmentRoles = useMemo(
    () => resolveAssignmentRoles(selectedTopRole, selectedSubRole),
    [selectedTopRole, selectedSubRole]
  );
  const isAccountTeamSelected = isVideoAccountTeamTopRole(selectedTopRole);
  const isAllAccountTeamSubroles =
    isAccountTeamSelected && isVideoAccountTeamAllSubrole(selectedSubRole);
  const selectedRole = assignmentRoles[0] || selectedSubRole;
  const roleVideos = useMemo(
    () =>
      isAllAccountTeamSubroles
        ? mergeVideosAcrossRoles(videosByRole, assignmentRoles)
        : videosByRole[selectedRole] || [],
    [assignmentRoles, isAllAccountTeamSubroles, selectedRole, videosByRole]
  );
  const roleLabel = isAllAccountTeamSubroles
    ? "Account Team (all subroles)"
    : getRoleLabel(selectedRole);
  const topRoleLabel = getTopRoleLabel(selectedTopRole);
  const libraryCount = (libraryItems as unknown[]).length;

  const topRoleOptions = useMemo(
    () =>
      VIDEO_ROLE_TOP_OPTIONS.map(role => ({
        value: role.value,
        label: role.label,
      })),
    []
  );

  const subRoleOptions = useMemo(
    () =>
      VIDEO_ACCOUNT_TEAM_SUBROLE_OPTIONS.map(role => ({
        value: role.value,
        label: role.label,
      })),
    []
  );

  const savedGroups = useMemo<VideoGroup[]>(
    () => toTopicGroups(roleVideos),
    [roleVideos]
  );

  const filterTabs = useMemo(() => {
    const uncategorized = savedGroups.find(
      group => group.key === UNCATEGORIZED_TOPIC_KEY
    );
    return [
      { id: ALL_TOPICS_KEY, label: "All", count: roleVideos.length },
      ...savedGroups
        .filter(group => group.key !== UNCATEGORIZED_TOPIC_KEY)
        .map(group => ({
          id: group.key,
          label: group.label,
          count: group.videos.length,
        })),
      ...(uncategorized && uncategorized.videos.length > 0
        ? [
            {
              id: UNCATEGORIZED_TOPIC_KEY,
              label: uncategorized.label,
              count: uncategorized.videos.length,
            },
          ]
        : []),
    ];
  }, [roleVideos.length, savedGroups]);

  // Selected role: topic sections after search (home-page play order).
  const topicGroups = useMemo(
    () =>
      savedGroups
        .map(group => ({
          ...group,
          videos: group.videos.filter(video => matchesSearch(video, query)),
        }))
        .filter(group => !query || group.videos.length > 0),
    [savedGroups, query]
  );

  const canvasSections = useMemo(
    () =>
      topicGroups.filter(
        group =>
          group.videos.length > 0 || (isMagnetic && group.key === topicFilter)
      ),
    [topicGroups, isMagnetic, topicFilter]
  );

  const sectionKeys = useMemo(
    () =>
      isEditing
        ? (editGroups || []).map(group => group.key)
        : canvasSections.map(group => group.key),
    [isEditing, editGroups, canvasSections]
  );

  const hasOrderChanges =
    isEditing &&
    flattenIds(editGroups).join() !== flattenIds(savedGroups).join();

  const assignedVideoIdsByLibrary = useMemo(() => {
    const map: Record<string, string[]> = {};
    assignmentRoles.forEach(role => {
      (videosByRole[role] || []).forEach(video => {
        if (!video.library_video_id) return;
        const existing = map[video.library_video_id] || [];
        if (!existing.includes(video.video_id)) {
          map[video.library_video_id] = [...existing, video.video_id];
        }
      });
    });
    return map;
  }, [assignmentRoles, videosByRole]);

  /** Library video → account-team subrole labels (All browse). */
  const roleLabelsByLibraryId = useMemo(() => {
    if (!isAllAccountTeamSubroles) return {} as Record<string, string[]>;
    const map: Record<string, string[]> = {};
    ACCOUNT_TEAM_ASSIGNMENT_ROLES.forEach(role => {
      const label = getRoleLabel(role);
      (videosByRole[role] || []).forEach(video => {
        if (!video.library_video_id) return;
        const existing = map[video.library_video_id] || [];
        if (!existing.includes(label)) {
          map[video.library_video_id] = [...existing, label];
        }
      });
    });
    return map;
  }, [isAllAccountTeamSubroles, videosByRole]);

  const getAssignedRoleLabels = (video: Video): string[] => {
    if (!isAllAccountTeamSubroles) return [];
    if (video.library_video_id) {
      return roleLabelsByLibraryId[video.library_video_id] || [];
    }
    return [getVideoRoleLabel(video)];
  };

  const getAssignmentIdsForVideo = (video: Video): string[] => {
    if (
      isAllAccountTeamSubroles &&
      video.library_video_id &&
      assignedVideoIdsByLibrary[video.library_video_id]
    ) {
      return assignedVideoIdsByLibrary[video.library_video_id];
    }
    return [video.video_id];
  };

  const isVideoFullyPublished = (video: Video): boolean => {
    if (!isAllAccountTeamSubroles || !video.library_video_id) {
      return video.is_active ?? true;
    }
    const rows: Video[] = [];
    assignmentRoles.forEach(role => {
      (videosByRole[role] || []).forEach(row => {
        if (row.library_video_id === video.library_video_id) rows.push(row);
      });
    });
    return rows.length > 0 && rows.every(row => row.is_active ?? true);
  };

  const removeScopeLabel = isAllAccountTeamSubroles
    ? "all account team subroles"
    : null;
  const statusScopeNote = isAllAccountTeamSubroles
    ? "for all account team subroles"
    : undefined;

  const registerSection = useMagneticSections({
    sectionKeys,
    activeKey: topicFilter,
    enabled: isMagnetic,
    scrollRequestId,
    scrollRootRef: canvasRef,
    onActiveChange: setTopicFilter,
    allKey: ALL_TOPICS_KEY,
  });

  const refreshAll = () =>
    Promise.all([
      dispatch(fetchAdminVideos({})),
      dispatch(fetchLibraryVideos()),
    ]);

  const resetRoleBrowse = () => {
    setEditGroups(null);
    setTopicFilter(ALL_TOPICS_KEY);
    canvasRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSelectTopRole = (roleKey: string) => {
    if (isEditing && hasOrderChanges) {
      toast.error("Save or discard the order changes first");
      return;
    }
    setSelectedTopRole(roleKey);
    if (isVideoAccountTeamTopRole(roleKey)) {
      const keepConcrete =
        !!selectedSubRole &&
        ACCOUNT_TEAM_ASSIGNMENT_ROLES.includes(selectedSubRole) &&
        !isVideoAccountTeamAllSubrole(selectedSubRole);
      setSelectedSubRole(
        keepConcrete ? selectedSubRole : DEFAULT_ACCOUNT_TEAM_SUBROLE
      );
    }
    resetRoleBrowse();
  };

  const handleSelectSubRole = (roleKey: string) => {
    if (isEditing && hasOrderChanges) {
      toast.error("Save or discard the order changes first");
      return;
    }
    setSelectedSubRole(roleKey);
    resetRoleBrowse();
  };

  const handleTopicChange = (topicId: string) => {
    setTopicFilter(topicId);
    if (topicId === ALL_TOPICS_KEY) {
      canvasRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setScrollRequestId(id => id + 1);
  };

  const handleGroupChange = (groupKey: string, nextVideos: Video[]) =>
    setEditGroups(prev =>
      (prev ?? savedGroups).map(group =>
        group.key === groupKey ? { ...group, videos: nextVideos } : group
      )
    );

  const handleGroupReorder = (
    groupKey: string,
    index: number,
    direction: ReorderDirection
  ) => {
    const group = (editGroups ?? savedGroups).find(g => g.key === groupKey);
    const movedId = group?.videos[index]?.video_id;
    runSmoothReorder(listReorderRefs.current[groupKey], () => {
      setEditGroups(prev =>
        (prev ?? savedGroups).map(item => {
          if (item.key !== groupKey) return item;
          return {
            ...item,
            videos: recalculateVideoOrders(
              moveItemInArray(item.videos, index, direction)
            ),
          };
        })
      );
    });
    if (movedId) flash(movedId);
  };

  const exitEditing = () => {
    setEditGroups(null);
  };

  const handleSaveOrder = async () => {
    if (!editGroups || isAllAccountTeamSubroles) return;
    const result = await dispatch(
      reorderAdminVideos({
        role: selectedRole,
        video_ids: flattenIds(editGroups),
      })
    );
    if (reorderAdminVideos.fulfilled.match(result)) {
      await dispatch(fetchAdminVideos({}));
      toast.success("Video order saved");
      exitEditing();
    }
  };

  const removeRoleLabel = removeScopeLabel
    ? removeScopeLabel
    : removeTarget
      ? getVideoRoleLabel(removeTarget)
      : roleLabel;

  const handleConfirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    const assignmentIds = getAssignmentIdsForVideo(removeTarget);
    let failed = false;
    for (const videoId of assignmentIds) {
      const result = await dispatch(deleteVideo(videoId));
      if (deleteVideo.rejected.match(result)) {
        toast.error((result.payload as string) || "Failed to remove video");
        failed = true;
        break;
      }
    }
    setRemoving(false);
    if (failed) return;
    toast.success(
      isAllAccountTeamSubroles
        ? "Removed from all account team subroles"
        : `Removed from ${removeRoleLabel}`
    );
    setRemoveTarget(null);
    setEditGroups(null);
    await refreshAll();
  };

  const handleConfirmStatus = async () => {
    if (!statusTarget) return;
    setStatusSaving(true);
    const assignmentIds = getAssignmentIdsForVideo(statusTarget.video);
    let failed = false;
    for (let index = 0; index < assignmentIds.length; index += 1) {
      const isLast = index === assignmentIds.length - 1;
      const result = await dispatch(
        updateVideoActiveStatus({
          video_id: assignmentIds[index],
          is_active: statusTarget.toActive,
          quiet: !isLast || isAllAccountTeamSubroles,
        })
      );
      if (updateVideoActiveStatus.rejected.match(result)) {
        failed = true;
        break;
      }
    }
    setStatusSaving(false);
    if (failed) {
      setStatusTarget(null);
      return;
    }
    if (isAllAccountTeamSubroles) {
      toast.success(
        statusTarget.toActive
          ? "Published for all account team subroles"
          : "Unpublished for all account team subroles"
      );
    }
    setEditGroups(prev =>
      prev
        ? prev.map(group => ({
            ...group,
            videos: recalculateVideoOrders(
              group.videos.map(video =>
                assignmentIds.includes(video.video_id) ||
                (statusTarget.video.library_video_id &&
                  video.library_video_id ===
                    statusTarget.video.library_video_id)
                  ? { ...video, is_active: statusTarget.toActive }
                  : video
              )
            ),
          }))
        : prev
    );
    await dispatch(fetchAdminVideos({}));
    setStatusTarget(null);
  };

  const renderListRow = (
    video: Video,
    options?: {
      index?: number;
      groupKey?: string;
      groupLength?: number;
      showOrder?: boolean;
    }
  ) => {
    const thumbnailUrl = getYouTubeThumbnailUrl(video.video_url);
    const isPublished = isVideoFullyPublished(video);
    const isReorderRow = options?.showOrder === true;
    const index = options?.index ?? -1;
    const groupLength = options?.groupLength ?? 0;
    const groupKey = options?.groupKey;
    const statusLabel = isAllAccountTeamSubroles
      ? isPublished
        ? "Unpublish for all subroles"
        : "Publish for all subroles"
      : isPublished
        ? "Unpublish"
        : "Publish";
    const deleteLabel = isAllAccountTeamSubroles
      ? "Remove from all subroles"
      : `Remove from ${getVideoRoleLabel(video)}`;

    return (
      <div
        key={video.video_id}
        data-reorder-id={isReorderRow ? video.video_id : undefined}
        className={`library-video-row library-video-row--assignment${
          isReorderRow ? " library-video-row--reorder" : ""
        }${
          isReorderRow && isHighlighted(video.video_id)
            ? " is-reorder-highlight"
            : ""
        }`}
        role="listitem"
      >
        <button
          type="button"
          className="library-video-row__thumb"
          onClick={() => setPreviewVideo(video)}
          title="Preview video"
        >
          {isReorderRow && (video.video_order || 0) > 0 && (
            <span className="library-video-card__order-badge">
              {video.video_order}
            </span>
          )}
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt={video.title || "Video"} />
          ) : (
            <Play size={18} className="text-gray-300" />
          )}
        </button>
        <div className="library-video-row__main">
          <TruncatedTooltip
            tooltip={video.title || "Untitled Video"}
            className="library-video-card__title"
          >
            {video.title || "Untitled Video"}
          </TruncatedTooltip>
          {video.description && (
            <TruncatedTooltip
              tooltip={video.description}
              className="library-video-card__description"
            >
              {video.description}
            </TruncatedTooltip>
          )}
          {isAllAccountTeamSubroles &&
            getAssignedRoleLabels(video).length > 0 && (
              <p className="library-video-row__role">
                {getAssignedRoleLabels(video).join(" · ")}
              </p>
            )}
        </div>
        <span
          className={`library-video-row__status${
            isPublished ? "" : " library-video-row__status--off"
          }`}
        >
          {isPublished ? "Published" : "Unpublished"}
        </span>
        {isReorderRow && groupKey ? (
          <ReorderControls
            index={index}
            length={groupLength}
            disabled={!isPublished}
            onMove={direction => handleGroupReorder(groupKey, index, direction)}
          />
        ) : (
          <ItemActionsMenu
            ariaLabel="Video actions"
            actions={[
              {
                id: "status",
                label: statusLabel,
                icon: isPublished ? EyeOff : Power,
                onClick: () =>
                  setStatusTarget({ video, toActive: !isPublished }),
              },
              {
                id: "remove",
                label: deleteLabel,
                icon: Trash2,
                danger: true,
                onClick: () => setRemoveTarget(video),
              },
            ]}
          />
        )}
      </div>
    );
  };

  const renderTopicVideos = (list: Video[]) =>
    layout === "list" ? (
      <div className="library-video-list" role="list">
        {list.map(video => renderListRow(video))}
      </div>
    ) : (
      <RoleVideoGrid
        gridClassName="topic-video-rail"
        videos={list}
        isOrderEditMode={false}
        onVideosChange={noop}
        onPreview={setPreviewVideo}
        onDelete={setRemoveTarget}
        deleteTitle={
          isAllAccountTeamSubroles
            ? "Remove from all subroles"
            : "Remove from role"
        }
        getSharedRoleLabels={
          isAllAccountTeamSubroles ? getAssignedRoleLabels : undefined
        }
        getIsActive={
          isAllAccountTeamSubroles ? isVideoFullyPublished : undefined
        }
        getStatusActionLabel={
          isAllAccountTeamSubroles
            ? (_video, isActive) =>
                isActive
                  ? "Unpublish for all subroles"
                  : "Publish for all subroles"
            : undefined
        }
        onStatusToggle={(video, toActive) =>
          setStatusTarget({ video, toActive })
        }
      />
    );

  const renderCanvas = () => {
    if (loading && roleVideos.length === 0) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-purple-600" />
        </div>
      );
    }

    if (isEditing) {
      const isListEdit = layout === "list";
      return (
        <>
          <div className="reorder-banner" role="note">
            <span className="reorder-banner__icon">
              {isListEdit ? (
                <ArrowDownUp size={16} />
              ) : (
                <GripVertical size={16} />
              )}
            </span>
            <div>
              <p className="reorder-banner__title">
                {isListEdit
                  ? `Use move actions to reorder ${roleLabel} videos`
                  : `Drag cards to reorder ${roleLabel} videos`}
              </p>
              <p className="reorder-banner__text">
                Each topic is a tab on the home page and plays in this order.
                Unpublished videos stay out of the order. Click Save Order when
                you&apos;re done.{" "}
                <span className="reorder-banner__tip">
                  {isListEdit
                    ? "To drag and drop instead, discard, switch to card view, then click Edit Order."
                    : "To use move up/down controls instead, discard, switch to list view, then click Edit Order."}
                </span>
              </p>
            </div>
          </div>
          <div className="video-sections">
            {editGroups.map(group => (
              <section
                key={group.key}
                ref={registerSection(group.key)}
                className={`video-section${
                  topicFilter === group.key ? " is-active" : ""
                }`}
              >
                <h4 className="video-section__hdr">
                  {group.label}
                  {group.videos[0]?.topic_is_active === false && (
                    <span className="topic-row__status normal-case tracking-normal">
                      Hidden from users
                    </span>
                  )}
                  <span className="video-section__count">
                    {group.videos.length}
                  </span>
                </h4>
                {isListEdit ? (
                  <div
                    className="library-video-list"
                    role="list"
                    ref={node => {
                      listReorderRefs.current[group.key] = node;
                    }}
                  >
                    {group.videos.map((video, index) =>
                      renderListRow(video, {
                        index,
                        groupKey: group.key,
                        groupLength: group.videos.length,
                        showOrder: true,
                      })
                    )}
                  </div>
                ) : (
                  <SortableVideoGrid
                    gridClassName="admin-video-container__grid library-video-grid"
                    videos={group.videos}
                    highlightId={highlightId}
                    onMoved={flash}
                    onVideosChange={nextVideos =>
                      handleGroupChange(group.key, nextVideos)
                    }
                    onPreview={setPreviewVideo}
                    onDelete={setRemoveTarget}
                    deleteTitle={`Remove from ${roleLabel}`}
                    onStatusToggle={(video, toActive) =>
                      setStatusTarget({ video, toActive })
                    }
                  />
                )}
              </section>
            ))}
          </div>
        </>
      );
    }

    if (roleVideos.length === 0) {
      return (
        <EmptyState
          icon={UserCheck}
          title={`No videos assigned to ${roleLabel}`}
          description={
            libraryCount === 0
              ? "Add videos in the Video Library tab, then assign them here."
              : "Choose roles, then pick videos from the library to show them."
          }
        >
          {libraryCount > 0 && (
            <button
              type="button"
              className="topics-empty__suggest"
              onClick={() => setAssignOpen(true)}
            >
              <Plus size={14} />
              Assign videos
            </button>
          )}
        </EmptyState>
      );
    }

    if (canvasSections.length === 0) {
      return (
        <EmptyState
          icon={SearchX}
          title={
            query ? (
              <>No videos match &quot;{search.trim()}&quot;</>
            ) : (
              "No videos match these filters"
            )
          }
          description={
            query
              ? "Try a different search term."
              : "Try a different topic or search term."
          }
        />
      );
    }

    return (
      <div className="video-sections">
        {canvasSections.map(group => (
          <section
            key={group.key}
            ref={registerSection(group.key)}
            className={`video-section${
              topicFilter === group.key ? " is-active" : ""
            }`}
          >
            <h4 className="video-section__hdr">
              {group.label}
              {group.videos[0]?.topic_is_active === false && (
                <span className="topic-row__status normal-case tracking-normal">
                  Hidden from users
                </span>
              )}
              <span className="video-section__count">
                {group.videos.length}
              </span>
            </h4>
            {group.videos.length > 0 ? (
              renderTopicVideos(group.videos)
            ) : (
              <p className="video-section__empty">
                No videos in this topic yet.
              </p>
            )}
          </section>
        ))}
      </div>
    );
  };

  return (
    <div className="admin-video-workspace__panel">
      {actionsSlot &&
        createPortal(
          <>
            <WorkspaceSearch
              value={search}
              onChange={setSearch}
              placeholder="Search Videos..."
              ariaLabel={`Search videos assigned to ${roleLabel}`}
              disabled={isEditing}
            />
            {isEditing ? (
              <>
                <Button
                  variant="secondary"
                  icon={<RotateCcw size={18} />}
                  onClick={exitEditing}
                >
                  Discard
                </Button>
                <Button
                  variant="primary"
                  icon={<Save size={18} />}
                  loading={reorderVideosLoading}
                  disabled={!hasOrderChanges}
                  onClick={handleSaveOrder}
                >
                  Save Order
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  disabled={isAllAccountTeamSubroles || roleVideos.length < 2}
                  title={
                    isAllAccountTeamSubroles
                      ? "Pick a subrole to edit play order"
                      : `Edit ${roleLabel} order`
                  }
                  onClick={() => {
                    if (isAllAccountTeamSubroles) return;
                    setSearch("");
                    // Topic "All" is browse-only; Edit Order jumps to the first topic chip
                    // so the filter bar matches sectioned reorder.
                    if (topicFilter === ALL_TOPICS_KEY) {
                      const firstTopicChip = filterTabs.find(
                        tab => tab.id !== ALL_TOPICS_KEY
                      );
                      if (firstTopicChip) setTopicFilter(firstTopicChip.id);
                    }
                    setEditGroups(savedGroups);
                    canvasRef.current?.scrollTo({ top: 0 });
                  }}
                  icon={<ArrowDownUp size={18} />}
                >
                  Edit Order
                </Button>
                <Button
                  variant="primary"
                  icon={<Plus size={18} />}
                  disabled={libraryCount === 0}
                  title="Choose roles, then assign videos from the library"
                  onClick={() => setAssignOpen(true)}
                >
                  {roleVideos.length > 0 ? "Edit Assignments" : "Assign Videos"}
                </Button>
              </>
            )}
          </>,
          actionsSlot
        )}
      <div className="library-browse">
        <aside className="library-topic-nav" aria-label="Topics">
          <p className="library-topic-nav__title">Topics</p>
          <FilterChips
            chips={filterTabs}
            activeId={topicFilter}
            onChange={handleTopicChange}
            ariaLabel="Jump to topic videos"
            orientation="vertical"
          />
        </aside>

        <div className="library-browse__main">
          <div className="library-browse__toolbar">
            <div className="library-browse__toolbar-actions">
              <DropdownSwitcher
                className={`w-60 shrink-0${isEditing ? " pointer-events-none opacity-60" : ""}`}
                value={selectedTopRole}
                prefix="Role: "
                options={topRoleOptions}
                onChange={handleSelectTopRole}
                ariaLabel={`Role: ${topRoleLabel}. Change role filter.`}
                listboxLabel="Role"
              />
              {isAccountTeamSelected && (
                <DropdownSwitcher
                  className={`min-w-[11.5rem] w-auto shrink-0${isEditing ? " pointer-events-none opacity-60" : ""}`}
                  value={selectedSubRole}
                  prefix="Sub Role: "
                  options={subRoleOptions}
                  onChange={handleSelectSubRole}
                  ariaLabel={`Sub Role: ${getRoleLabel(selectedSubRole)}. Change account team subrole.`}
                  listboxLabel="Sub Role"
                />
              )}
              <LayoutToggle
                value={layout}
                onChange={setLayout}
                disabled={isEditing}
              />
            </div>
          </div>
          <div className="admin-video-workspace__canvas" ref={canvasRef}>
            {renderCanvas()}
          </div>
        </div>
      </div>

      <AssignVideosModal
        isOpen={assignOpen}
        onClose={() => setAssignOpen(false)}
        initialTopRole={selectedTopRole}
        initialSubRole={selectedSubRole}
        videosByRole={videosByRole}
        onAssigned={({ topRole, subRole }) => {
          setSelectedTopRole(topRole);
          setSelectedSubRole(subRole);
          resetRoleBrowse();
        }}
      />

      <VideoPreviewModal
        video={previewVideo}
        onClose={() => setPreviewVideo(null)}
      />

      <DeactivateVideoModal
        isOpen={!!statusTarget}
        toggle={() => setStatusTarget(null)}
        videoTitle={statusTarget?.video.title || ""}
        isActivating={statusTarget?.toActive ?? false}
        onConfirm={handleConfirmStatus}
        loading={statusSaving}
        scopeNote={statusScopeNote}
      />

      <CommonModal
        isOpen={!!removeTarget}
        toggle={() => setRemoveTarget(null)}
        title={
          isAllAccountTeamSubroles
            ? "Remove from all account team subroles"
            : `Remove from ${removeRoleLabel}`
        }
        size="md"
        showHeader={true}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRemoveTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={removing}
              onClick={handleConfirmRemove}
            >
              Remove
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          {isAllAccountTeamSubroles
            ? "This video will be removed from every account team subrole (AE, CSM, SE, etc.)."
            : `${removeRoleLabel} users will no longer see`}{" "}
          {!isAllAccountTeamSubroles && (
            <>
              &quot;{removeTarget?.title || "this video"}&quot;. The video stays
              in the library and can be assigned again.
            </>
          )}
          {isAllAccountTeamSubroles && (
            <>
              &quot;{removeTarget?.title || "this video"}&quot; stays in the
              library and can be assigned again.
            </>
          )}
        </p>
      </CommonModal>
    </div>
  );
};

export default AssignmentsPanel;
